import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskedInView } from "../lib/anim";

const BREAK_IMG = "https://images.unsplash.com/photo-1482424917728-d82d29662023?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwZGFyayUyMG1vb2R5JTIwc2lsaG91ZXR0ZXxlbnwwfHx8fDE3ODgxMjY2MzR8MA&ixlib=rb-4.1.0&q=85";

const NOISE_WORDS = ["Thinking.", "Planning.", "Working.", "Scrolling.", "Performing.", "Doing."];
const RETURN_WORDS = ["Slow down.", "Breathe.", "Notice.", "Touch.", "Connect.", "Receive.", "Be here."];

const BENEFITS = [
  "Greater ability to relax after stress",
  "Feeling calmer and more grounded",
  "Greater emotional regulation",
  "Increased awareness of physical sensations",
  "Greater ability to remain present",
  "More resilience during stressful moments",
  "Greater capacity for rest and recovery",
  "Increased mind-body awareness",
  "Greater emotional flexibility",
  "Greater capacity for connection",
  "Increased awareness of personal boundaries",
  "Greater awareness of tension held in the body",
  "Greater capacity for intentional touch",
  "Greater ability to move between stimulation and relaxation",
];

export function Manifesto() {
  return (
    <section data-testid="manifesto-section" className="relative bg-[#050505] px-6 py-28 sm:py-40">
      <div className="mx-auto max-w-4xl text-center">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#f7e7ce]/50 ff-sans">Chapter 01 — The Noise</p>
        </Reveal>
        <h2 className="mt-8 ff-serif font-light leading-[0.95] text-[#fdfbf7]">
          <MaskedInView delay={0.1}>
            <span className="block text-[11vw] sm:text-6xl lg:text-7xl">YOUR BODY ISN'T</span>
          </MaskedInView>
          <MaskedInView delay={0.2}>
            <span className="block text-[11vw] sm:text-6xl lg:text-7xl">A MACHINE.</span>
          </MaskedInView>
          <MaskedInView delay={0.35} className="mt-4">
            <span className="block text-[13vw] sm:text-7xl lg:text-8xl italic gold-text">IT'S A PORTAL.</span>
          </MaskedInView>
        </h2>

        <Reveal delay={0.2} className="mt-16">
          <p className="text-sm sm:text-base font-light leading-relaxed text-[#f7e7ce]/70 ff-sans">
            We spend so much of our lives in our heads.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap items-baseline justify-center gap-x-6 gap-y-3">
          {NOISE_WORDS.map((w, i) => (
            <MaskedInView key={w} delay={0.1 + i * 0.08}>
              <span className="ff-serif text-2xl sm:text-3xl font-light text-[#f7e7ce]/45">{w}</span>
            </MaskedInView>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-14">
          <p className="text-sm sm:text-base font-light text-[#f7e7ce]/70 ff-sans">
            And sometimes we forget how to simply…
          </p>
          <p className="mt-4 ff-serif text-6xl sm:text-7xl font-light italic gold-text">FEEL.</p>
        </Reveal>

        <Reveal delay={0.15} className="mt-16">
          <p className="text-sm sm:text-base font-light leading-relaxed text-[#f7e7ce]/70 ff-sans">
            Pleasure Portal is an invitation to interrupt the noise.
          </p>
        </Reveal>

        <div className="mt-8 flex flex-wrap items-baseline justify-center gap-x-5 gap-y-2">
          {RETURN_WORDS.map((w, i) => (
            <MaskedInView key={w} delay={0.08 + i * 0.07}>
              <span className="ff-serif text-xl sm:text-2xl font-light text-[#f7e7ce]/80">{w}</span>
            </MaskedInView>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-16 space-y-2">
          <p className="ff-serif text-2xl sm:text-3xl font-light text-[#f7e7ce]/60">Not another thing to accomplish.</p>
          <p className="ff-serif text-2xl sm:text-3xl font-light italic gold-text">A ritual to experience.</p>
        </Reveal>
      </div>
    </section>
  );
}

export function ImageBreak() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} data-testid="image-break-section" className="relative h-[70vh] overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-[-14%]">
        <img
          src="/images/bottle.jpg"
          alt="Pleasure Portal artwork — golden celestial light, hibiscus and crystals"
          className="h-full w-full object-cover object-[50%_42%]"
          loading="lazy"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 60%, rgba(212,175,55,0.22), rgba(5,5,5,0.55) 65%)" }} />
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
        <MaskedInView>
          <span className="ff-serif block text-[10vw] sm:text-6xl lg:text-7xl font-light text-[#fdfbf7]">LESS THINKING.</span>
        </MaskedInView>
        <MaskedInView delay={0.2}>
          <span className="ff-serif block text-[10vw] sm:text-6xl lg:text-7xl font-light italic gold-text">MORE FEELING.</span>
        </MaskedInView>
      </div>
    </section>
  );
}

export function NervousSystem() {
  return (
    <section data-testid="nervous-system-section" className="bg-[#fdfbf7] px-6 py-28 text-[#050505] sm:py-40">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <div>
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.4em] text-[#a8842c] ff-sans font-medium">The Body Knows.</p>
            </Reveal>
            <h2 className="mt-6 ff-serif text-4xl sm:text-5xl font-light leading-[1.02] tracking-tight">
              <MaskedInView delay={0.1}>
                <span className="block">WHEN YOUR NERVOUS SYSTEM CAN MOVE BETWEEN ACTIVATION AND REST…</span>
              </MaskedInView>
            </h2>
            <MaskedInView delay={0.25} className="mt-6">
              <span className="ff-serif block text-3xl sm:text-4xl font-light italic text-[#a8842c]">
                you experience life differently.
              </span>
            </MaskedInView>
            <Reveal delay={0.2} className="mt-10 space-y-5">
              <p className="text-sm sm:text-base font-light leading-relaxed text-[#050505]/70 ff-sans">
                Nervous-system regulation describes our ability to respond appropriately to stimulation and
                stress — and return toward a more settled state afterward.
              </p>
              <p className="text-sm sm:text-base font-light leading-relaxed text-[#050505]/70 ff-sans">
                A greater capacity for regulation is generally associated with benefits such as:
              </p>
            </Reveal>
          </div>

          <div className="grid gap-px bg-[#d4af37]/25 sm:grid-cols-2">
            {BENEFITS.map((b, i) => (
              <Reveal key={b} delay={0.05 + (i % 4) * 0.08} y={20}>
                <div
                  data-testid={`benefit-card-${i + 1}`}
                  className="group flex h-full items-start gap-3 bg-[#fdfbf7] p-5 transition-colors duration-500 hover:bg-[#f7e7ce]/60"
                >
                  <span className="mt-0.5 text-[#d4af37] transition-transform duration-500 group-hover:rotate-90 group-hover:text-[#ff007f]">✦</span>
                  <p className="text-[13px] font-light leading-relaxed text-[#050505]/80 ff-sans">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15} className="mt-14 border-t border-[#d4af37]/30 pt-6">
          <p data-testid="nervous-system-disclaimer" className="text-[11px] leading-relaxed text-[#050505]/50 ff-sans">
            These are educational statements about nervous-system regulation in general. Pleasure Portal does not
            treat, cure or regulate any medical condition.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
